import { useCallback } from "react";

/**
 * Hook for managing integration-modal state in the URL.
 *
 * The open modal's source is carried as a `connect` query param on whatever
 * page opened it (e.g. `?connect=gmail`), so the same convention works in any
 * app without dedicated routes. The OAuth callback params the backend appends
 * (`success`, `oauthCallback`, `reason`) survive the round-trip and drive the
 * post-connect step.
 */
export const CONNECT_PARAM = "connect";

export const useIntegrationURL = () => {
  const parseSourceFromURL = useCallback(
    () => new URLSearchParams(window.location.search).get(CONNECT_PARAM),
    [],
  );

  const checkOAuthCallback = useCallback(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const success = searchParams.get("success") === "true";
    const oauthCallback = searchParams.get("oauthCallback") === "true";
    const reason = searchParams.get("reason");

    return {
      isOAuthCallback: oauthCallback,
      success,
      reason,
      cleanupParams: () => {
        searchParams.delete("success");
        searchParams.delete("oauthCallback");
        searchParams.delete("reason");
        return searchParams;
      },
    };
  }, []);

  const shouldOpenFromURL = useCallback(
    (sourceId: string) => parseSourceFromURL() === sourceId,
    [parseSourceFromURL],
  );

  // Add ?connect=<source> to the current URL, preserving path + other params.
  const setModalURL = useCallback((sourceId: string) => {
    const params = new URLSearchParams(window.location.search);
    if (params.get(CONNECT_PARAM) === sourceId) return;
    params.set(CONNECT_PARAM, sourceId);
    window.history.replaceState(
      null,
      "",
      `${window.location.pathname}?${params.toString()}`,
    );
  }, []);

  // Drop the modal + OAuth params, keeping the page and any unrelated params.
  const clearModalURL = useCallback(() => {
    const params = new URLSearchParams(window.location.search);
    [CONNECT_PARAM, "success", "oauthCallback", "reason"].forEach((key) =>
      params.delete(key),
    );
    const search = params.toString();
    window.history.replaceState(
      null,
      "",
      `${window.location.pathname}${search ? `?${search}` : ""}`,
    );
  }, []);

  return {
    parseSourceFromURL,
    shouldOpenFromURL,
    setModalURL,
    clearModalURL,
    checkOAuthCallback,
  };
};
